// Spec: specs/stats/questions/data_lab/storytelling_notes/functional_requirements.md §Draft lifecycle
//       specs/stats/questions/functional_requirements.md §6.2 Question title and annotation

import { useEffect, useRef, useState } from 'react'

import draftStore, {
  draftKey,
  Draft,
  DraftKeyParts
} from '@aytm/app/stats/page/question-views/components/lib/drafts/draftStore'
import { draftsMatch } from '@aytm/app/stats/page/question-views/components/lib/drafts/draftRecord'

const DRAFT_WRITE_DELAY_MS = 1000

interface DebugPage {
  lib?: { isDebugOn?: (scopes: string[]) => boolean }
  logger: { log: (...args: unknown[]) => void }
}

// the same debug switch as the view's localSave logging
export const logDraft = (...args: unknown[]): void => {
  const page = (window as unknown as { StatPage?: DebugPage }).StatPage
  page?.lib?.isDebugOn?.(['storytelling', 'all']) && page.logger.log(...args)
}

export interface EditDraftOptions extends DraftKeyParts {
  // false keeps the hook inert: nothing is read, written or marked
  enabled?: boolean
  editing: boolean
  // the item is leaving the report, so nothing more is written for it
  removed?: boolean
  // the editor is about to close, so the timed write waits; an interruption still keeps the draft
  paused?: boolean
  memoryOnly?: boolean
  // the typed state as a draft; called when a write happens, so it may read refs
  typed: () => Draft
  saved: Draft
}

export interface EditDraft {
  hasDraft: boolean
  read(): Draft | null
  // an interruption: the typed state stays as the draft, or the draft goes when nothing differs
  keep(): void
  discard(reason: string): void
}

type Snapshot = Required<Pick<EditDraftOptions, 'kind' | 'id' | 'enabled' | 'editing' | 'removed' | 'memoryOnly'>> &
  Pick<EditDraftOptions, 'typed' | 'saved'> & { key: string }

// a report switch unmounts the row; the cleanup below keeps an open edit as its draft
export default function useEditDraft(options: EditDraftOptions): EditDraft {
  const { kind, surveyId, userId, reportId, id, enabled = true, editing, removed = false, paused = false } = options
  const key = draftKey({ kind, surveyId, userId, reportId, id })
  const memoryOnly = Boolean(options.memoryOnly)

  // the key, storage rule and saved values of the report the edit began in: every write lands and compares there
  const edit = useRef({ key, memoryOnly, saved: options.saved })
  if (!editing || key === edit.current.key) edit.current = { key, memoryOnly, saved: options.saved }

  const [hasDraft, setHasDraft] = useState(() => enabled && null !== draftStore.read(key))
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // assigned during render, so a listener registered on any render writes the latest typed state
  const latest = useRef<Snapshot>(null as unknown as Snapshot)
  latest.current = {
    key: edit.current.key,
    kind,
    id,
    enabled,
    editing,
    removed,
    memoryOnly: edit.current.memoryOnly,
    typed: options.typed,
    saved: edit.current.saved
  }

  const clearTimer = (): void => {
    if (null !== timer.current) clearTimeout(timer.current)
    timer.current = null
  }

  const persist = (): void => {
    const snapshot = latest.current
    clearTimer()
    if (!snapshot.enabled) return
    // a write timed before the removal can still fire after it
    if (snapshot.removed) return

    const draft = snapshot.typed()
    if (draftsMatch(draft, snapshot.saved)) {
      draftStore.remove(snapshot.key)
      logDraft('persistStorytellingDraft remove:', snapshot.id)
      return
    }

    draftStore.write(snapshot.key, draft, { memoryOnly: snapshot.memoryOnly })
    logDraft(`persistStorytellingDraft ${snapshot.kind}:`, snapshot.id)
    logDraft('persistStorytellingDraft draft:', draft)
    logDraft('persistStorytellingDraft memoryOnly:', snapshot.memoryOnly)
  }

  // whether the current key holds a draft, re-read after every write
  const refresh = (): void => setHasDraft(enabled && null !== draftStore.read(key))

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled])

  // every change to the typed state moves the write: one draft write a second after the last change
  const typedState = enabled && editing ? JSON.stringify(options.typed()) : ''
  useEffect(() => {
    clearTimer()
    if (!enabled || !editing || removed || paused) return
    timer.current = setTimeout(() => {
      persist()
      refresh()
    }, DRAFT_WRITE_DELAY_MS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, editing, removed, paused, typedState])

  useEffect(() => {
    if (!enabled || !editing) return
    const flush = () => {
      persist()
      refresh()
    }
    window.addEventListener('pagehide', flush)
    return () => window.removeEventListener('pagehide', flush)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, editing])

  // the row unmounting mid-edit ends the edit without discarding it, unless the item itself was removed
  useEffect(
    () => () => {
      if (latest.current.enabled && latest.current.editing && !latest.current.removed) persist()
      clearTimer()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const read = (): Draft | null => (enabled ? draftStore.read(key) : null)

  const keep = (): void => {
    persist()
    refresh()
  }

  const discard = (reason: string): void => {
    clearTimer()
    if (!enabled) return
    draftStore.remove(edit.current.key)
    setHasDraft(false)
    logDraft(`discardStorytellingDraft ${reason}:`, id)
  }

  return { hasDraft, read, keep, discard }
}
